import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Download } from "lucide-react";
import { toast } from "react-toastify";
import accessURL from "../../../../components/utils/superAccess";
import { exportCustomersToExcel } from "../../../../components/utils/tableExport";
import {
  CustomerData,
  Filters,
  PaginationInfo,
} from "../../../../salesRep/components/customers/ctypes";
import Actions from "../../../../salesRep/components/customers/actions/Actions";
import FilterSection from "../../../../salesRep/components/customers/FilterSection";
import { customStyles } from "../../../../components/utils/utils";
import { useParams } from "react-router-dom";

const Table: React.FC = () => {
  const { customID } = useParams<{ customID: string }>();
  const [data, setData] = useState<CustomerData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchCustomers = async (page: number, pageSize: number) => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
    if (filters.search) params.name = filters.search;
    if (filters.category !== undefined)
      params.category = String(filters.category);
    if (filters.city !== undefined) params.city = String(filters.city);
    if (filters.state !== undefined) params.state = String(filters.state);

    const queryParams = new URLSearchParams(params);
    let url = `super-admin/getSingleCompanyCustomers/${customID}?page=${page}&limit=${pageSize}${
      queryParams.toString() ? `&${queryParams.toString()}` : ""
    }`;

    try {
      const response = await accessURL.get<{
        data: CustomerData[];
        pagination: PaginationInfo;
      }>(url);
      setData(response.data.data);
      setTotalRows(response.data.pagination.totalCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(currentPage, perPage);
  }, [currentPage, perPage, filters]);

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleExportCustomers = async () => {
    if (!customID) return;

    try {
      setIsExporting(true);
      await exportCustomersToExcel({
        client: accessURL,
        endpoint: `super-admin/getSingleCompanyCustomers/${customID}`,
        filters,
        fileName: `super-admin-company-customers-${
          new Date().toISOString().split("T")[0]
        }.csv`,
        sheetName: "Customers",
      });
      toast.success("Customers exported successfully!");
    } catch (error) {
      console.error("Error exporting customers:", error);
      toast.error("Failed to export customers.");
    } finally {
      setIsExporting(false);
    }
  };

  const getCategoryStyle = (category: string) => {
    switch (category.toUpperCase()) {
      case "A":
        return "bg-green-500 text-white font-bold py-1 px-2 rounded";
      case "B":
        return "bg-yellow-500 text-white font-bold py-1 px-2 rounded";
      case "C":
        return "bg-black text-white font-bold py-1 px-2 rounded";
      default:
        return "bg-gray-500 text-white font-bold py-1 px-2 rounded";
    }
  };

  // Main table columns (essential fields)
  const columns: TableColumn<CustomerData>[] = [
    {
      name: "S/N",
      selector: (row) => row.id,
      format: (_, index) => (currentPage - 1) * perPage + index + 1,
      width: "70px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "270px",
    },
    {
      name: "Annual Target",
      selector: (row) => row.target,
      width: "170px",
    },
    {
      name: "Specialization",
      selector: (row) => row.specialization,
      width: "270px",
    },
    {
      name: "Category",
      selector: (row) => row.category,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className={getCategoryStyle(row.category)}>{row.category}</span>
      ),
    },
    {
      name: "Call Rate",
      selector: (row) => {
        const rate = Number(row.call_rate) * 100;
        return isNaN(rate) ? "0%" : rate === 0 ? "0" : `${rate.toFixed(2)}%`;
      },
      width: "120px",
    },
    {
      name: "Actions",
      width: "500px",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => {
              setSelectedCustomer(row);
              setIsModalOpen(true);
            }}
            className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
          >
            View Details
          </button>
        </div>
      ),
    },
  ];

  const CustomerDetailsModal = () => {
    if (!selectedCustomer || !isModalOpen) return null;

    const details = [
      { label: "State", value: selectedCustomer.state },
      { label: "City", value: selectedCustomer.city },
      { label: "Email", value: selectedCustomer.email },
      { label: "Phone", value: selectedCustomer.phone },
      { label: "Place of Practice", value: selectedCustomer.pop },
      { label: "Nature of Business", value: selectedCustomer.nature },
      { label: "Planned Visits", value: selectedCustomer.planned },
      { label: "Achieved", value: selectedCustomer.achieved },
      { label: "Target", value: selectedCustomer.target },
      {
        label: "Call Rate",
        value: `${(selectedCustomer.call_rate * 100).toFixed(2)}%`,
      },
      {
        label: "Date Created",
        value: new Intl.DateTimeFormat("en-US", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(selectedCustomer.created_at)),
      },
    ];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                Customer Details: {selectedCustomer.name}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              {details.map((detail, index) => (
                <div key={index} className="mb-2">
                  <div className="font-semibold text-gray-600">
                    {detail.label}
                  </div>
                  <div className="text-gray-900">{detail.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 bg-gray-100">
      <div className="py-6">
        <FilterSection onFilterChange={handleFilterChange} />
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportCustomers}
          disabled={loading || isExporting || totalRows === 0 || !customID}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          {isExporting ? "Exporting..." : "Export Customers"}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        sortServer
        paginationPerPage={perPage}
        striped
        highlightOnHover
        responsive
        persistTableHead
        customStyles={customStyles}
        noDataComponent={
          <div className="p-4 text-center text-gray-500">
            No Customers found
          </div>
        }
      />

      <CustomerDetailsModal />
    </div>
  );
};

export default Table;
